/**
 * The sticky top bar: only the dates (presets, complete days, custom range).
 * Every other filter lives in the "Advanced filters" drawer (FilterDrawer.jsx),
 * opened from the button on the right; what is applied shows as chips below.
 */

import { useCallback, useRef, useState } from "react";
import { DATE_PRESETS, MAX_WINDOW_DAYS, daysBetween, windowProblem, windowProblemMessage } from "../../shared/dates.js";
import { fmtDate } from "../lib/format.js";
import { FILTER_LABELS } from "../lib/scope.js";
import { advancedCount } from "../lib/tabFilters.js";
import { FilterDrawer } from "./FilterDrawer.jsx";
import { useFilterOptions } from "../api/hooks.js";
import { Button, Checkbox, Icon, RangeCalendar, Segmented, popoverClass, rangeText, useDismiss } from "./ui/index.js";

const SHORT = { "7d": "7D", "28d": "28D", "90d": "90D", mtd: "MTD", ytd: "YTD", "365d": "12M" };
const PRESET_OPTIONS = DATE_PRESETS.map((p) => ({ value: p.id, label: SHORT[p.id] ?? p.label, title: p.label }));

/** Date presets and the "Complete days" switch: in the bar on wider screens, in the date picker on phones. */
function Presets({ filters, setPreset, setComplete }) {
  return (
    <>
      <Segmented label="Date presets" options={PRESET_OPTIONS} value={filters.preset} onChange={setPreset} />
      <span title="Presets end yesterday, so every day shown is a whole day. Off: presets run to today, which is still in progress.">
        <Checkbox label="Complete days" checked={filters.complete} onChange={(e) => setComplete(e.target.checked)} className="text-[11.5px] whitespace-nowrap" />
      </span>
    </>
  );
}

function WindowPicker({ filters, setRange, today, setPreset, setComplete }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ from: filters.from, to: filters.to });
  const wrap = useRef(null);
  useDismiss(wrap, open, useCallback(() => setOpen(false), []));
  // Mid-selection (start picked, no end yet) is not a problem, just unfinished.
  const problem = draft.from && draft.to ? windowProblem(draft.from, draft.to, today) : null;
  const days = daysBetween(filters.from, filters.to) + 1;

  return (
    <div className="relative" ref={wrap}>
      <button
        type="button"
        onClick={() => {
          setDraft({ from: filters.from, to: filters.to });
          setOpen((o) => !o);
        }}
        className={`tabular inline-flex h-10 items-center gap-2 rounded-control border px-3.5 text-[12px] font-medium whitespace-nowrap text-ink transition-colors ${
          filters.preset === "custom" ? "border-accent/40 bg-accent-soft" : "border-line-default bg-surface-1 hover:border-line-strong"
        }`}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <Icon name="calendar" className="size-3.5 text-ink-3" />
        {fmtDate(filters.from)} – {fmtDate(filters.to)}
        <span className="rounded-full bg-surface-2 px-1.5 text-[10.5px] text-ink-3">{days}d</span>
        <Icon name="chevronDown" className={`size-3.5 text-ink-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div role="dialog" aria-label="Date range" className={`absolute top-full right-0 mt-1.5 w-max p-4 ${popoverClass}`}>
          <div className="mb-3 flex flex-col items-start gap-2 sm:hidden">
            <Presets filters={filters} setPreset={setPreset} setComplete={setComplete} />
          </div>
          <RangeCalendar value={draft} onChange={setDraft} max={today} />
          <div className="mt-3 flex items-center justify-between gap-3 border-t border-line pt-3">
            <span className="tabular text-[11px]" style={{ color: problem ? "var(--status-critical)" : "var(--text-muted)" }}>
              {problem
                ? windowProblemMessage(problem)
                : !draft.from
                  ? "Pick a start date"
                  : !draft.to
                    ? "Now pick an end date"
                    : `${rangeText(draft)} · ${daysBetween(draft.from, draft.to) + 1}d · IST · max ${MAX_WINDOW_DAYS} days`}
            </span>
            <div className="flex gap-1.5">
              {/* Clears the picked days to start a new range; nothing applies until Apply. */}
              <Button size="xs" variant="ghost" disabled={!draft.from && !draft.to} onClick={() => setDraft({ from: "", to: "" })}>
                Reset
              </Button>
              <Button size="xs" variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                size="xs"
                variant="primary"
                disabled={Boolean(problem) || !draft.from || !draft.to}
                onClick={() => {
                  setRange(draft.from, draft.to);
                  setOpen(false);
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Every filter that is set, as a removable pill beside the Advanced filters
 * button (the mockup's "Audience  All ▾" pills). Filters this page does not
 * offer (kept in the URL from another page) are listed apart as "not used on
 * this page": they do not change these numbers (audit MET-02).
 */
function ActiveFilters({ f }) {
  const { filters, update, clear, pageKeys, unusedHere, activeCount } = f;
  const opts = useFilterOptions(filters.tenant, filters.from, filters.to);
  // A value's display name where the options carry one (Format: "medwiki" → "MedWiki").
  const shown = (k) => opts.data?.[k]?.find((o) => o.value === filters[k])?.label ?? filters[k];
  if (!activeCount) return null;
  const used = pageKeys.filter((k) => filters[k]);
  const chip = (k, muted) => (
    <span
      key={k}
      className={`inline-flex h-8 animate-fade-in items-center gap-1.5 rounded-full border py-1 pr-1 pl-2.5 text-[11.5px] ${muted ? "border-dashed border-line-default bg-surface-1 opacity-75" : "border-accent/25 bg-accent-soft"}`}
    >
      <span className="text-ink-3">{FILTER_LABELS[k] ?? k}</span>
      <span className={`font-medium ${muted ? "text-ink-3" : "text-accent-ink"}`}>{shown(k)}</span>
      <button
        type="button"
        onClick={() => update({ [k]: "" })}
        aria-label={`Remove ${FILTER_LABELS[k] ?? k} filter`}
        className="grid size-5 place-items-center rounded-full text-ink-3 transition-colors hover:bg-surface-1 hover:text-ink"
      >
        <Icon name="close" className="size-3" strokeWidth={1.8} />
      </button>
    </span>
  );
  return (
    <>
      {used.map((k) => chip(k, false))}
      {unusedHere.length > 0 && (
        <span className="text-[11px] text-ink-3" title="Set on another page. This page does not offer these filters, so they do not change its numbers.">
          Not used on this page:
        </span>
      )}
      {unusedHere.map((k) => chip(k, true))}
      <button type="button" onClick={clear} className="text-[11.5px] font-medium text-ink-3 underline-offset-2 hover:text-ink hover:underline">
        Clear all
      </button>
    </>
  );
}

/** `show` lists the filter groups the current page offers: platform | profile | utm. */
export function FilterBar({ f, show = ["platform", "profile"] }) {
  const { filters, setPreset, setRange, setComplete, today } = f;
  const [drawer, setDrawer] = useState(false);
  const trigger = useRef(null);
  const count = advancedCount(filters, show);
  const close = () => {
    setDrawer(false);
    trigger.current?.focus();
  };

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
      <button
        ref={trigger}
        type="button"
        onClick={() => setDrawer(true)}
        aria-haspopup="dialog"
        aria-expanded={drawer}
        className={`inline-flex h-10 items-center gap-1.5 rounded-control border px-3.5 text-[12px] font-medium transition-colors ${
          count ? "border-accent/40 bg-accent-soft text-accent-ink" : "border-line-default bg-surface-1 text-ink hover:border-line-strong"
        }`}
      >
        <Icon name="filter" className="size-3.5" />
        Advanced filters
        {count > 0 && <span className="tabular grid h-4 min-w-4 place-items-center rounded-full bg-accent px-1 text-[10px] font-semibold text-on-accent">{count}</span>}
      </button>
      <ActiveFilters f={f} />

      <div className="ml-auto flex flex-wrap items-center gap-3">
        {/* Phones: presets live in the date picker, so the bar stays one line. */}
        <div className="hidden items-center gap-3 sm:flex">
          <Presets filters={filters} setPreset={setPreset} setComplete={setComplete} />
        </div>

        <WindowPicker key={`${filters.from}-${filters.to}`} filters={filters} setRange={setRange} today={today} setPreset={setPreset} setComplete={setComplete} />
      </div>

      <FilterDrawer f={f} show={show} open={drawer} onClose={close} />
    </div>
  );
}
